import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { getRoundedValueOfPercentage } from '@sg/garnish';

import type { TipsGroup } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useCheckoutContext } from '../../../../../CheckoutProvider';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns checkout tips and the corresponding helpers based on
 * the active cart, checkout payload and other options.
 */
export const useCheckoutTips = (params: UseCheckoutTipsParams = {}) => {
  const { withNoTipOption = false } = params;

  const { cart } = useCheckoutContext();
  const { t, formatPrice } = useLocalizationContext();

  // ─────────────────────────────────────────────

  const orderSubtotal = cart?.ledger?.subtotal ?? 0;

  // ─────────────────────────────────────────────

  const tenPercentTipValue = TIPS['10'];
  const tenPercentTipLabel = `${tenPercentTipValue}%`;
  const tenPercentTipAmount = getRoundedValueOfPercentage(
    tenPercentTipValue,
    orderSubtotal,
  );
  const tenPercentTipAmountFormatted = formatPrice(tenPercentTipAmount, 'USD');

  const fifteenPercentTipValue = TIPS['15'];
  const fifteenPercentTipLabel = `${fifteenPercentTipValue}%`;
  const fifteenPercentTipAmount = getRoundedValueOfPercentage(
    fifteenPercentTipValue,
    orderSubtotal,
  );
  const fifteenPercentTipAmountFormatted = formatPrice(
    fifteenPercentTipAmount,
    'USD',
  );

  const twentyPercentTipValue = TIPS['20'];
  const twentyPercentTipLabel = `${twentyPercentTipValue}%`;
  const twentyPercentTipAmount = getRoundedValueOfPercentage(
    twentyPercentTipValue,
    orderSubtotal,
  );
  const twentyPercentTipAmountFormatted = formatPrice(
    twentyPercentTipAmount,
    'USD',
  );

  // ─────────────────────────────────────────────

  const tips = useMemo<Tips>(
    () => [
      ...(withNoTipOption
        ? [
            {
              value: `${TIPS.NO_TIP}`,
              label: t('checkout.tips.no-tip-label'),
              amount: 0,
              accessibilityHint: t('checkout.tips.no-tip-a11y-hint'),
            },
          ]
        : []),
      {
        value: `${tenPercentTipValue}`,
        label: tenPercentTipLabel,
        helperText: tenPercentTipAmountFormatted,
        amount: tenPercentTipAmount,
        accessibilityHint: t('checkout.tips.tip-a11y-hint', {
          tip: tenPercentTipLabel,
          amount: tenPercentTipAmountFormatted,
        }),
      },
      {
        value: `${fifteenPercentTipValue}`,
        label: fifteenPercentTipLabel,
        helperText: fifteenPercentTipAmountFormatted,
        amount: fifteenPercentTipAmount,
        accessibilityHint: t('checkout.tips.tip-a11y-hint', {
          tip: fifteenPercentTipLabel,
          amount: fifteenPercentTipAmountFormatted,
        }),
      },
      {
        value: `${twentyPercentTipValue}`,
        label: twentyPercentTipLabel,
        helperText: formatPrice(twentyPercentTipAmount, 'USD'),
        amount: twentyPercentTipAmount,
        accessibilityHint: t('checkout.tips.tip-a11y-hint', {
          tip: twentyPercentTipLabel,
          amount: twentyPercentTipAmountFormatted,
        }),
      },
      {
        value: TIPS.CUSTOM,
        label: t('checkout.tip.custom'),
        accessibilityHint: t('checkout.tips.custom-tip-a11y-hint'),
      },
    ],
    [
      t,
      formatPrice,
      withNoTipOption,
      tenPercentTipValue,
      tenPercentTipLabel,
      tenPercentTipAmountFormatted,
      tenPercentTipAmount,
      fifteenPercentTipValue,
      fifteenPercentTipLabel,
      fifteenPercentTipAmountFormatted,
      fifteenPercentTipAmount,
      twentyPercentTipValue,
      twentyPercentTipLabel,
      twentyPercentTipAmount,
      twentyPercentTipAmountFormatted,
    ],
  );

  // ─────────────────────────────────────────────

  return {
    orderSubtotal,
    tips,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TIPS = {
  NO_TIP: 0,
  '10': 10,
  '15': 15,
  '20': 20,
  CUSTOM: 'custom',
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseCheckoutTipsParams = {
  readonly withNoTipOption?: boolean;
};

type Tips = ComponentProps<typeof TipsGroup>['tips'];
